import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import { AISummaryAnswer } from "./ai-summary-answer";
import { AISummaryLoadingAnimation } from "./ai-summary-loading-animation";
import { AIAnswerSearchBorder } from "./styled";

export interface Source {
    title: string;
    url: string;
}

export const AISummary = ({ query }: { query: string }) => {
    const csmAiBaseUrl =
        process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] ||
        "https://www.atlassian.com/gateway/api";
    const cloudId =
        process.env["SEARCH_AI_SUMMARY_CLOUD_SITE_ID"] ||
        "a35630ef-8504-4f3d-8e29-a2617e5d5b30";
    const agentId =
        process.env["SEARCH_AI_SUMMARY_AGENT_ID"] ||
        "3b8ff012-b386-319c-9ff3-f9cda66cb98e";

    const [isLoading, setIsLoading] = useState(true);
    const isFirstResponseRef = useRef(true);
    const [showAdditionalSummary, setShowAdditionalSummary] = useState(false);
    const [traceResponse, setTraceResponse] = useState<string | undefined>(
        undefined
    );
    const abortControllerRef = useRef<AbortController | null>(null);
    const [aiSummary, setAiSummary] = useState<string>("");
    const [sources, setSources] = useState<Source[]>();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    // eslint-disable-next-line complexity
    const processLine = useCallback((line: string) => {
        try {
            const parsed = JSON.parse(line);
            if (parsed.type === "TRACE" && parsed.message?.content) {
                // TRACE contains the initial loading messages
                let traceContent = parsed.message.content;

                if (traceContent.includes(": ")) {
                    traceContent = traceContent.split(": ")[1];
                }
                if (traceContent.endsWith(".")) {
                    traceContent = traceContent.slice(0, -1);
                }
                setTraceResponse(traceContent);
            } else if (
                parsed.type === "FINAL_RESPONSE" &&
                (parsed.message?.sources || parsed.message?.message.sources)
            ) {
                // FINAL_RESPONSE contains the entire summary, sources, and additional info LLM info
                const sources = (parsed.message?.sources ||
                    parsed.message?.message?.sources) as Source[];

                if (!sources || sources.length === 0) {
                    createAnalyticsEvent({
                        eventName: "searchAiSummaryNoSources",
                        eventType: "track",
                        action: "failed",
                        actionSubject: "searchAiSummary",
                        actionSubjectId: "searchAiSummaryFinalResponseLoaded",
                        source: "searchPage",
                        attributes: {
                            query: query,
                            conversationId:
                                parsed.message?.human_message
                                    ?.conversation_channel_id
                        }
                    }).fire();
                }

                setSources(sources);
                setTraceResponse(undefined);
            } else if (
                parsed.type === "ANSWER_PART" &&
                parsed.message?.content
            ) {
                setAiSummary((prev) => prev + parsed.message.content);
                setTraceResponse(undefined);
            }
        } catch (error) {
            console.warn("Failed to parse line as JSON:", line);
            setAiSummary((prev) => prev + "\n" + line);
        }
    }, []);

    const fetchAISummary = useCallback(
        // eslint-disable-next-line complexity
        async (query: string) => {
            setIsLoading(true);
            setShowAdditionalSummary(false);
            setTraceResponse(undefined);
            setSources(undefined);
            setAiSummary("");
            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();
            isFirstResponseRef.current = true;
            const userContext = {
                user_locale: intl.locale || "en",
                current_time: new Date().toISOString()
            };
            const queryWithUserContext = `${query} \n ${JSON.stringify(
                userContext
            )}`;

            let reader = null;
            try {
                const uuid = uuidv4();
                const response = await fetch(
                    `${csmAiBaseUrl}/assist/csm/v2/chat/channel/${uuid}/message/stream`,
                    {
                        method: "POST",
                        credentials: "omit",
                        signal: abortControllerRef.current?.signal,
                        headers: {
                            "x-cloudid": cloudId,
                            "x-product": "jira",
                            "x-experience-id": "csm-ai",
                            "content-type": "application/json;charset=UTF-8"
                        },
                        body: JSON.stringify({
                            content: {
                                version: 1,
                                type: "doc",
                                content: [
                                    {
                                        type: "paragraph",
                                        content: [
                                            {
                                                type: "text",
                                                text: queryWithUserContext
                                            }
                                        ]
                                    }
                                ]
                            },
                            recipient_agent_named_id: "atlassian_wac_agent",
                            agent_id: agentId,
                            mimeType: "text/adf",
                            store_message: false,
                            citations_enabled: true
                        })
                    }
                );

                createAnalyticsEvent({
                    eventName: "aiSummary",
                    eventType: "track",
                    action: "loaded",
                    actionSubject: "searchAiSummary",
                    actionSubjectId: "searchAiSummaryLoaded",
                    source: "searchPage",
                    eventContainer: "load",
                    attributes: {
                        responseCode: response.status,
                        responseBodyAvailable: response.body ? true : false
                    }
                }).fire();

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                if (!response.body) {
                    throw new Error(
                        "Response body is not available for streaming"
                    );
                }

                reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        if (buffer.trim()) {
                            processLine(buffer.trim());
                            setIsLoading(false);
                        }
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });
                    buffer += chunk;

                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        const trimmedLine = line.trim();
                        if (trimmedLine) {
                            processLine(trimmedLine);
                            setIsLoading(false);
                        }
                    }
                }
            } catch (error) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                ) {
                    return;
                }
                console.error("Error processing AI summary stream:", error);
                setAiSummary(
                    (prev) =>
                        prev +
                        "\nAtlassian Intelligence can't provide a response right now."
                );
            } finally {
                reader?.releaseLock();
                setIsLoading(false);
            }
        },
        [createAnalyticsEvent, processLine]
    );

    useEffect(() => {
        fetchAISummary(query);
    }, [query, fetchAISummary]);

    const isLoadingOrTraceResponse = Boolean(isLoading || traceResponse);

    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.100")
            })}
        >
            <AIAnswerSearchBorder
                isLoading={isLoadingOrTraceResponse}
                isStreaming={!!traceResponse}
            >
                <div css={aiSummaryBoxStyle}>
                    {isLoadingOrTraceResponse && (
                        <AISummaryLoadingAnimation
                            traceResponse={traceResponse}
                        />
                    )}
                    {!isLoadingOrTraceResponse && (
                        <AISummaryAnswer
                            aiSummary={aiSummary}
                            sources={sources}
                            showAdditionalSummary={showAdditionalSummary}
                            setShowAdditionalSummary={setShowAdditionalSummary}
                        />
                    )}
                </div>
            </AIAnswerSearchBorder>
        </div>
    );
};

const aiSummaryBoxStyle = css({
    padding: token("space.150"),
    width: "100%",
    position: "relative"
});
